import { useState, useEffect } from 'react';
import { usewpdataSelectors } from 'lib/wpdataState/selectors';
import { useCartSelectors } from 'lib/cartState/selectors';
import Link from '../Button/Anchor/Anchor';
import parse from 'html-react-parser';
import './floating-bar.scss';
import glideImage from '../../utils/glideImage';
import { readCookie, writeCookie } from '../../utils/helpers';

const COOKIE_KEY = 'shipping_timer';
const COOLDOWN_MS = 24 * 60 * 60 * 1000;

const FloatingCTA = () => {
  const { Floater, Theme, Settings } = usewpdataSelectors();
  const { CartData } = useCartSelectors();

  const timerMinutes = Number(Theme?.timer || 30);
  const initialTime = Math.round(timerMinutes * 60);

  const [timeLeft, setTimeLeft] = useState(() => {
    const cookie = readCookie(COOKIE_KEY);
    const now = Date.now();
    if (cookie && cookie.endAt && cookie.cooldownExpires > now) {
      return Math.max(0, Math.floor((cookie.endAt - now) / 1000));
    }
    return initialTime;
  });

  const [timerLabel, setTimerLabel] = useState('');

  useEffect(() => {
    const now = Date.now();
    let cookie = readCookie(COOKIE_KEY);
    let endAt, cooldownExpires;
    if (cookie && cookie.endAt && cookie.cooldownExpires > now) {
      endAt = cookie.endAt;
      cooldownExpires = cookie.cooldownExpires;
    } else {
      endAt = now + initialTime * 1000;
      cooldownExpires = now + COOLDOWN_MS;
      writeCookie(COOKIE_KEY, { endAt, cooldownExpires }, cooldownExpires);
    }

    const intervalId = setInterval(() => {
      setTimeLeft((prev) => {
        const next = Math.max(prev - 1, 0);
        return next;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [initialTime]);

  useEffect(() => {
    const minutes = Math.floor((timeLeft % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (timeLeft % 60).toString().padStart(2, '0');
    const adjustedTime = Floater?.timerLabel?.replace('###react_time###', ` ${minutes}:${seconds} `) ?? '';
    setTimerLabel(adjustedTime);
  }, [timeLeft, Floater?.timerLabel]);

  return (
    <section
      id="floating-cta"
      style={{ background: Floater.styles.background }}
      className={`floating-cta ${CartData.isHeroVisible && 'floating-cta--active'}`}
    >
      <div className="floating-cta__wrapper">
        <div className="floating-cta__content">
          {Floater.floaterImage && (
            <img
              className="floating-cta__contentImage mon-img-64-w"
              src={glideImage(Floater.floaterImage, { w: 64, h: 64 })}
              alt={Settings.brandName}
            />
          )}

          <div className="floating-cta__contentRating">
            <img
              src={`${window.template_root}/stars/${Floater.starsImage}.svg`}
              className="floater__image--stars coarse"
              alt={Settings.brandName}
            />
            <span className="floating-cta__verbalScore" style={{ color: Floater.styles.textColorSecondary }}>
              {parse(Floater?.verbalScore)}
            </span>
          </div>
          <div
            className={`floating-cta__contentText mobile-none`}
            style={{ color: Floater.styles.textColorPrimary }}
            data-theme={Theme.heading}
          >
            {Floater.floaterHeading}
          </div>
        </div>
        <div className="floating-cta__ctaWrapper">
          <Link
            text={Floater.ctaLabel}
            data-cy="cta-floater"
            style={
              {
                background: Floater.styles.buttonBackground,
                '--floater-button-hover': Floater.styles.buttonHoverBackground,
              } as React.CSSProperties
            }
            className="floating-cta__cta"
          />
          {timeLeft > 0 && (
            <p className="floating-cta__timer" style={{ color: Floater.styles.textColorPrimary }}>
              <svg
                style={{ marginRight: '8px' }}
                width="20"
                height="13"
                viewBox="0 0 20 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M14.2578 0.931274C15.9918 1.33365 16.6551 2.3476 17.4867 3.74316H14.2578V0.931274ZM4.12415 3.86035C4.46365 3.86035 4.71008 4.02423 4.71008 4.40723C4.71008 4.73026 4.4487 4.99225 4.12582 4.99316H0.585938C0.262299 4.99316 0 5.25562 0 5.5791C0 5.90274 0.262299 6.16504 0.585938 6.16504H5.85938C6.18607 6.16504 6.4476 6.42719 6.4476 6.75098C6.4476 7.07462 6.1853 7.33691 5.86166 7.33691H0.585938C0.262299 7.33691 0 7.59921 0 7.92285C0 8.24649 0.262299 8.50879 0.585938 8.50879H1.875V10.2666C1.875 10.5902 2.1373 10.8525 2.46094 10.8525H3.63876C3.84872 11.8868 4.76257 12.6494 5.83984 12.6494C6.91711 12.6494 7.83096 11.8868 8.04092 10.8525H13.9903C14.2003 11.8868 15.1141 12.6494 16.1914 12.6494C17.2687 12.6494 18.1825 11.8868 18.3925 10.8525H19.4141C19.7377 10.8525 20 10.5902 20 10.2666V6.75098C20 5.03177 18.1866 4.91763 18.185 4.91504H13.6719C13.3482 4.91504 13.0859 4.65274 13.0859 4.3291V0.813477H2.46094C2.1373 0.813477 1.875 1.07578 1.875 1.39941V2.68848H1.17188C0.848236 2.68848 0.585938 2.95078 0.585938 3.27441C0.585938 3.59805 0.848236 3.86035 1.17188 3.86035H4.12415ZM16.951 9.64374C17.3705 10.0632 17.3705 10.7436 16.951 11.1631C16.2762 11.8378 15.1172 11.3578 15.1172 10.4033C15.1172 9.44904 16.2762 8.96899 16.951 9.64374ZM6.59943 9.64374C7.01889 10.0632 7.01889 10.7436 6.59943 11.1631C5.92468 11.8378 4.76562 11.3578 4.76562 10.4033C4.76562 9.44904 5.92468 8.96899 6.59943 9.64374Z"
                  fill={Floater.styles.textColorPrimary}
                />
              </svg>
              {parse(timerLabel)}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default FloatingCTA;
